import React, { useEffect, useState } from 'react';
import {
  Satellite, ShieldCheck, IdCard, CreditCard, BarChart3, ScrollText,
  Smartphone, FolderSearch, BellRing, ServerCog,
} from 'lucide-react';
import PortalShell from '@/components/portal/PortalShell';
import PublicHero from '@/components/portal/PublicHero';
import { EmptyState } from '@/components/eds';
import { contentService, type FeatureItem } from '@/services/contentService';

// WP2.1B-R2: rebuilt to the LOCKED Features composition (giisdecsite_30 · features).
// Presentation-only. Feature cards are driven by contentService.listFeatures()
// (geo-api → governed published fallback); no feature content is hard-coded. The
// card grid, reliability board and status pills are the locked composition.

const CARD_ICONS = [Satellite, ShieldCheck, IdCard, CreditCard, BarChart3, ScrollText, Smartphone, FolderSearch, BellRing];

// P9B: architectural security guarantees, NOT a live health/uptime board.
// These are design facts (true by construction), not real-time "Online" status —
// no status endpoint backs a live claim, so none is asserted.
const RELIABILITY = [
  { name: 'Role-based access', state: 'Enforced server-side' },
  { name: 'Audit logging', state: 'On every request' },
  { name: 'Encrypted transport', state: 'HTTPS · secure sign-in' },
];

const Features: React.FC = () => {
  const [items, setItems] = useState<FeatureItem[]>([]);
  useEffect(() => { contentService.listFeatures().then(setItems); }, []);

  const features = Array.isArray(items) ? items : [];

  return (
    <PortalShell>
      <PublicHero
        badge="Platform Features"
        title={<>A secure portal,<br /><span className="text-eds-mint">engineered for trust</span></>}
        subtitle="Role-based access, audit logging and national data governance at the core of every feature."
      />

      <section className="py-16 md:py-20 bg-eds-bg">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {features.length === 0 ? (
            <EmptyState
              title="Capabilities are being published"
              message="Approved platform features will appear here once released by GIISDEC."
            />
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {features.map((f, i) => {
                const Icon = CARD_ICONS[i % CARD_ICONS.length];
                return (
                  <div
                    key={f.id}
                    className="bg-eds-surface border border-eds-border rounded-eds-lg p-7 shadow-eds-sm hover:shadow-eds-md transition"
                  >
                    <div className="w-12 h-12 rounded-eds-md bg-eds-success/10 border border-eds-success/20 flex items-center justify-center text-eds-emerald-2">
                      <Icon className="w-6 h-6" />
                    </div>
                    <h3 className="font-eds-display font-bold text-eds-fg mt-5">{f.title}</h3>
                    <p className="text-sm text-eds-muted-fg mt-2 leading-relaxed">{f.description}</p>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </section>

      {/* Reliability & Compliance */}
      <section className="py-16 md:py-20 bg-eds-forest text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
          <div>
            <p className="text-xs font-semibold uppercase tracking-widest text-eds-mint">Reliability &amp; Compliance</p>
            <h2 className="font-eds-display text-3xl md:text-4xl font-bold mt-3 tracking-[-0.01em]">
              Secure by design, audited end to end
            </h2>
            <p className="text-white/70 mt-5 leading-relaxed max-w-lg">
              Every action is recorded for audit, with role-based access enforced server-side across
              the platform. GIISDEC GIIP is hosted at the NITA National Data Centre.
            </p>
          </div>

          <div className="space-y-3">
            {RELIABILITY.map((r) => (
              <div key={r.name} className="flex items-center justify-between rounded-eds-md bg-white/5 border border-white/10 px-5 py-4">
                <span className="font-medium text-white/90">{r.name}</span>
                <span className="inline-flex items-center gap-2 text-sm text-eds-mint font-semibold">
                  <ShieldCheck className="w-4 h-4" aria-hidden="true" />
                  {r.state}
                </span>
              </div>
            ))}
            <div className="flex items-center gap-2 text-xs text-white/60 pt-2">
              <ServerCog className="w-4 h-4 text-eds-mint" />
              Hosted at the NITA National Data Centre.
            </div>
          </div>
        </div>
      </section>
    </PortalShell>
  );
};

export default Features;
